import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
  WhatsApp as WhatsAppIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import { api } from '../../services/api';
import { formatarDataLog2, formatarData } from '../../utils/formatToView';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface SorteioHistorico {
  id: number;
  cliente: {
    id: number;
    nome: string;
    whatsapp?: string;
  };
  usuario: {
    id: number;
    nome: string;
  };
  fatura?: {
    id: number;
    valor: number | string;
    data_vencimento: string;
    status: string;
  };
  data_sorteio: string;
  premio_aplicado: boolean;
  data_premio_aplicado?: string;
  whatsapp_enviado: boolean;
  data_whatsapp_enviado?: string;
  configuracao_utilizada: any;
}

interface SorteadorHistoricoModalProps {
  open: boolean;
  onClose: () => void;
}

const SorteadorHistoricoModal: React.FC<SorteadorHistoricoModalProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [historico, setHistorico] = useState<SorteioHistorico[]>([]);
  const [dataInicio, setDataInicio] = useState<Date | null>(() => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth(), 1); // Primeiro dia do mês atual
  });
  const [dataFim, setDataFim] = useState<Date | null>(() => {
    const hoje = new Date();
    return new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0); // Último dia do mês atual
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      carregarHistorico();
    }
  }, [open, dataInicio, dataFim]);

  const carregarHistorico = async () => {
    if (!dataInicio || !dataFim) return;

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        data_inicio: dataInicio.toISOString().split('T')[0],
        data_fim: dataFim.toISOString().split('T')[0],
      });

      const response = await api.get(`/sorteador/historico?${params}`) as ApiResponse<SorteioHistorico[]>;
      
      if (response.success) {
        setHistorico(response.data);
      } else {
        setError(response.message || 'Erro ao carregar histórico');
      }
    } catch (error: any) {
      console.error('Erro ao carregar histórico:', error);
      setError(error.response?.data?.message || 'Erro interno ao carregar histórico');
    } finally {
      setIsLoading(false);
    }
  };  

  const getStatusColor = (premioAplicado: boolean, whatsappEnviado: boolean) => {
    if (premioAplicado && whatsappEnviado) return 'success';
    if (premioAplicado || whatsappEnviado) return 'warning';
    return 'default';
  };

  const getStatusText = (premioAplicado: boolean, whatsappEnviado: boolean) => {
    if (premioAplicado && whatsappEnviado) return 'Completo';
    if (premioAplicado) return 'Premiado';
    if (whatsappEnviado) return 'Notificado';
    return 'Pendente';
  };

  const formatarValor = (valor: number | string | undefined): string => {
    if (valor === undefined || valor === null) return '0.00';
    
    if (typeof valor === 'number') {
      return valor.toFixed(2);
    }
    
    if (typeof valor === 'string') {
      const numero = parseFloat(valor);
      return isNaN(numero) ? '0.00' : numero.toFixed(2);
    }
    
    return '0.00';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box display="flex" alignItems="center">
          <TrophyIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Histórico de Sorteios</Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Filtros */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
            <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Filtros de Data
          </Typography>
          
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={8}>
                <SysDateRangePicker
                  onChange={(range: { startDate: Date | null; endDate: Date | null }) => {
                    setDataInicio(range.startDate);
                    setDataFim(range.endDate);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={carregarHistorico}
                  disabled={isLoading || !dataInicio || !dataFim}
                  fullWidth
                >
                  {isLoading ? <CircularProgress size={20} /> : 'Buscar'}
                </Button>
              </Grid>
            </Grid>
        </Box>

        {/* Mensagens de erro */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Lista de resultados */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        ) : historico.length > 0 ? (
          <Grid container spacing={2}>
            {historico.map((item) => (
              <Grid item xs={12} key={item.id}>
                <Card variant="outlined" sx={{ '&:hover': { boxShadow: 2 } }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" flex={1}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          <PersonIcon />
                        </Avatar>
                        <Box flex={1}>
                          <Typography variant="h6" gutterBottom>
                            {item.cliente.nome}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Sorteado em: {formatarDataLog2(item.data_sorteio)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Por: {item.usuario.nome}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={getStatusText(item.premio_aplicado, item.whatsapp_enviado)}
                          color={getStatusColor(item.premio_aplicado, item.whatsapp_enviado)}
                          size="small"
                        />
                        
                        {item.premio_aplicado && (
                          <Chip
                            icon={<PaymentIcon />}
                            label="Premiado"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        )}
                        
                        {item.whatsapp_enviado && (
                          <Chip
                            icon={<WhatsAppIcon />}
                            label="WhatsApp"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>

                                         {/* Detalhes da Fatura */}
                     {item.fatura && (
                       <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                         <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 1 }}>
                           📄 Informações da Fatura Premiada
                         </Typography>
                         <Grid container spacing={2}>
                           <Grid item xs={12} sm={4}>
                             <Box sx={{ 
                               p: 1.5, 
                               bgcolor: 'primary.light', 
                               borderRadius: 1,
                               textAlign: 'center'
                             }}>
                               <Typography variant="caption" color="white" display="block">
                                 ID da Fatura
                               </Typography>
                               <Typography variant="h6" color="white" fontWeight="bold">
                                 #{item.fatura.id}
                               </Typography>
                             </Box>
                           </Grid>
                           <Grid item xs={12} sm={4}>
                             <Box sx={{ 
                               p: 1.5, 
                               bgcolor: 'success.light', 
                               borderRadius: 1,
                               textAlign: 'center'
                             }}>
                               <Typography variant="caption" color="white" display="block">
                                 Valor
                               </Typography>
                                                               <Typography variant="h6" color="white" fontWeight="bold">
                                  R$ {formatarValor(item.fatura.valor)}
                                </Typography>
                             </Box>
                           </Grid>
                           <Grid item xs={12} sm={4}>
                             <Box sx={{ 
                               p: 1.5, 
                               bgcolor: 'warning.light', 
                               borderRadius: 1,
                               textAlign: 'center'
                             }}>
                               <Typography variant="caption" color="white" display="block">
                                 Vencimento
                               </Typography>
                               <Typography variant="h6" color="white" fontWeight="bold">
                                 {formatarData(item.fatura.data_vencimento)}
                               </Typography>
                             </Box>
                           </Grid>
                         </Grid>
                       </Box>
                     )}

                     {/* Detalhes adicionais */}
                     <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                       <Grid container spacing={2}>
                         {item.premio_aplicado && item.data_premio_aplicado && (
                           <Grid item xs={12} sm={6}>
                             <Typography variant="body2" color="text.secondary">
                               <strong>Premiado em:</strong> {formatarDataLog2(item.data_premio_aplicado)}
                             </Typography>
                           </Grid>
                         )}
                         {item.whatsapp_enviado && item.data_whatsapp_enviado && (
                           <Grid item xs={12} sm={6}>
                             <Typography variant="body2" color="text.secondary">
                               <strong>WhatsApp enviado em:</strong> {formatarDataLog2(item.data_whatsapp_enviado)}
                             </Typography>
                           </Grid>
                         )}
                       </Grid>
                     </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box textAlign="center" sx={{ py: 4 }}>
            <TrophyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhum sorteio encontrado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Não foram encontrados sorteios no período selecionado.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SorteadorHistoricoModal;
